import {Injectable, Injector} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import Enumerable from "linq";
import {HealthErrorCodes, ServiceType, SystemStatusView} from "@models";
import {SimHealthMonitorService} from "../../../api/generated/service";
import {SimEnumExtendedService} from "./sim-enum.service";

export interface HealthProblemOptions {
	include: HealthErrorLocation;
}

export enum HealthErrorLocation {
	StartRecording = 1 << 0,
	ToolBar = 1 << 1,
	All = StartRecording | ToolBar,
}

export interface ComponentHealthErrors {
	serviceType: ServiceType;
	errorCodes: HealthErrorCodes[];
	errorMessages?: string[];
}

@Injectable({
	providedIn: "root",
})
export class SimHealthMonitorExtendedService extends SimHealthMonitorService {
	constructor(
		injector: Injector,
		private _translate: TranslateService,
		private _enumService: SimEnumExtendedService,
	) {
		super(injector);
	}

	private readonly criticalErrorCodes: ComponentHealthErrors[] = [
		{
			serviceType: ServiceType.SimAudioServer,
			errorCodes: [HealthErrorCodes.NotReachable, HealthErrorCodes.AsteriskAudioSocketNotRunning, HealthErrorCodes.LowDiskSpace],
		},
		{
			serviceType: ServiceType.SimRecordingServer,
			errorCodes: [HealthErrorCodes.NotReachable, HealthErrorCodes.FFmpegNotFound, HealthErrorCodes.LowDiskSpace],
		},
	];

	public hasComponentErrors(systemStatus: SystemStatusView, options: HealthProblemOptions): boolean {
		if (systemStatus == null) {
			return false;
		} else {
			const ret = this.filterComponentErrorCodes(systemStatus, options);
			return ret.length > 0;
		}
	}

	public getComponentErrors(systemStatus: SystemStatusView, options: HealthProblemOptions): ComponentHealthErrors[] {
		const ret = this.filterComponentErrorCodes(systemStatus, options);
		return ret;
	}

	private filterComponentErrorCodes(systemStatus: SystemStatusView, options: HealthProblemOptions): ComponentHealthErrors[] {
		const errorLocation = options?.include ?? HealthErrorLocation.All;

		const includeAll = this._enumService.containsFlags(errorLocation, HealthErrorLocation.All);
		const includeCriticalErrors = this._enumService.containsFlags(errorLocation, HealthErrorLocation.StartRecording);

		let filter: ComponentHealthErrors[] = [];
		if (includeCriticalErrors) {
			filter.push(...this.criticalErrorCodes);
		}

		const ret = Enumerable.from(systemStatus?.components ?? [])
			.select((component) => {
				const ret = <ComponentHealthErrors>{
					serviceType: component.serviceType,
					errorCodes: component.status.errorCodes,
				};

				if (!includeAll) {
					ret.errorCodes = ret.errorCodes.filter((healthErrorCode) =>
						filter.some((c) => c.serviceType === component.serviceType && c.errorCodes.includes(healthErrorCode)),
					);
				}

				ret.errorMessages = ret.errorCodes.map((errorCode) => this.getErrorMessage(errorCode));
				return ret;
			})
			.where((component) => component.errorCodes.length > 0)
			.toArray();

		return ret;
	}

	public getErrorMessage(healthErrorCode: HealthErrorCodes): string {
		return this._translate.instant("HealthError_" + HealthErrorCodes[healthErrorCode]);
	}
}
